import type { CandidatoCosto, CostosSku, FuenteCostoBase } from './types';
import { CodigoArticulo } from '../../components/CodigoArticulo';
import { fmtCosto, fmtDiferenciaPct } from './formato';

/** A partir de esta diferencia (en valor absoluto) entre costo por BOM y planilla COMBO se resalta. */
export const UMBRAL_DIFERENCIA_COMBO_PCT = 10;

export const FUENTE_COSTO_BASE: Record<FuenteCostoBase, string> = {
  lista_con_descuentos_erp: 'Lista con desc. ERP',
  bom: 'Costo por BOM',
  planilla_combo: 'Planilla COMBO',
};

export const CANDIDATOS: Array<{ id: CandidatoCosto; label: string; hint: string }> = [
  { id: 'listaProveedor',        label: 'Lista proveedor',      hint: 'Precio de lista del proveedor, sin descuentos ni IVA (artículos de proveedor)' },
  { id: 'listaConDescuentosErp', label: 'Lista con desc. ERP',  hint: 'Lista proveedor con Desc. Proveedor → Marca → SKU en cascada, como en la OC' },
  { id: 'listaConDescuentosErpContado', label: 'Lista con desc. ERP + contado', hint: 'Lista con desc. ERP y, al final, el Descuento contado del proveedor usado (no entra en el costo base)' },
  { id: 'precioUltimaCompra',   label: 'Precio última compra',    hint: 'Del catálogo' },
  { id: 'costoPromedio',        label: 'Costo promedio',          hint: 'Del catálogo' },
];

/** Costo por BOM vs. planilla COMBO, para combos (con o sin BOM cargado). */
export function ComparacionCombo({ data }: { data: CostosSku }) {
  const bom = data.esCombo ? data.costos.listaConDescuentosErp : null;
  const planilla = data.costoPlanillaCombo;
  const diff = data.diferenciaPct;
  const alerta = diff != null && Math.abs(diff) >= UMBRAL_DIFERENCIA_COMBO_PCT;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Combo: BOM vs. planilla COMBO</h3>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
            <th className="px-3 py-2">Costo</th>
            <th className="px-3 py-2 text-right">Sin IVA</th>
            <th className="px-3 py-2 text-right">Con IVA</th>
            <th className="px-3 py-2">Observaciones</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-t border-slate-100">
            <td className="px-3 py-2 text-slate-700" title="Suma de Lista con desc. ERP de los componentes × cantidad">Costo por BOM</td>
            <td className="px-3 py-2 text-right font-mono">{fmtCosto(bom?.sinIva)}</td>
            <td className="px-3 py-2 text-right font-mono">{fmtCosto(bom?.conIva)}</td>
            <td className="px-3 py-2 text-xs text-slate-500">
              {!data.esCombo ? 'Sin BOM cargado' : bom && bom.sinDato.length > 0 ? `Sin dato en: ${bom.sinDato.join(', ')}` : ''}
            </td>
          </tr>
          <tr className="border-t border-slate-100">
            <td className="px-3 py-2 text-slate-700" title="Costo neto de la solapa COMBO de la planilla de artículos de proveedor">Costo planilla COMBO</td>
            <td className="px-3 py-2 text-right font-mono">{fmtCosto(planilla?.sinIva)}</td>
            <td className="px-3 py-2 text-right font-mono">{fmtCosto(planilla?.conIva)}</td>
            <td className="px-3 py-2 text-xs text-slate-500">{planilla?.sinIva == null ? 'No está en la planilla COMBO' : ''}</td>
          </tr>
          <tr className={`border-t border-slate-100 ${alerta ? 'bg-red-50' : ''}`}>
            <td className="px-3 py-2 text-slate-700" title="(Costo por BOM − planilla COMBO) / planilla COMBO, sin IVA">Diferencia %</td>
            <td className={`px-3 py-2 text-right font-mono ${alerta ? 'font-semibold text-red-700' : ''}`}>{fmtDiferenciaPct(diff)}</td>
            <td className="px-3 py-2" />
            <td className="px-3 py-2 text-xs text-slate-500">
              {alerta ? `Difieren en ${UMBRAL_DIFERENCIA_COMBO_PCT}% o más: revisar el BOM o la planilla` : diff == null ? 'Falta alguno de los dos costos' : ''}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export function FilasComponentes({ c, nivel = 0 }: { c: CostosSku; nivel?: number }) {
  return (
    <>
      {c.componentes.map(h => (
        <FilaComponente key={`${nivel}-${h.sku}`} sku={h.sku} cantidad={h.cantidad} c={h.costos} nivel={nivel} />
      ))}
    </>
  );
}

export function FilaComponente({ sku, cantidad, c, nivel }: { sku: string; cantidad: number; c: CostosSku; nivel: number }) {
  return (
    <>
      <tr className="border-t border-slate-100">
        <td className="px-3 py-2 font-mono text-xs text-slate-700" style={{ paddingLeft: `${12 + nivel * 16}px` }}>
          {nivel > 0 && <span className="text-slate-300 mr-1">└</span>}<CodigoArticulo sku={sku} />
          {c.esCombo && <span className="ml-1 text-[10px] text-brand-600">combo</span>}
          {!c.encontrado && <span className="ml-1 text-[10px] text-red-500">no está en el catálogo</span>}
        </td>
        <td className="px-3 py-2 text-right font-mono text-xs">{cantidad}</td>
        {CANDIDATOS.map(k => (
          <td key={k.id} className="px-3 py-2 text-right font-mono text-xs">{fmtCosto(c.costos[k.id].sinIva)}</td>
        ))}
        <td className="px-3 py-2 text-xs text-slate-500">{c.proveedorUsado?.proveedor ?? '—'}</td>
      </tr>
      <FilasComponentes c={c} nivel={nivel + 1} />
    </>
  );
}
